import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  getAdminProducts, createProduct, updateProduct, deleteProduct,
  getAdminOrders, getOrderStats, updateOrderStatus
} from '../../services/wholesaleApi';
import { toast } from 'react-toastify';

const ITEM_TYPES = ['card','bracelet','keychain','sticker','tag','pendant','badge','other'];
const ORDER_STATUSES = ['pending','confirmed','processing','shipped','delivered','cancelled','refunded'];
const STATUS_COLORS = {
  pending:'badge-yellow', confirmed:'badge-blue', processing:'badge-purple',
  shipped:'badge-yellow', delivered:'badge-green', cancelled:'badge-red', refunded:'badge-grey'
};

// ── Product Modal ─────────────────────────────────────────────
function ProductModal({ product, onClose, onSaved }) {
  const isEdit = !!product?._id;
  const [form, setForm] = useState({
    name: '', itemType: 'tag', shortDescription: '', description: '',
    basePrice: '', material: '', dimensions: '', weight: '',
    minOrderQuantity: 1, maxOrderQuantity: 10000, leadTimeDays: 7,
    includesQrCode: true, nfcEnabled: false, inStock: true,
    features: '', colors: '', tags: '',
    tiers: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (product) setForm({
      name: product.name || '', itemType: product.itemType || 'tag',
      shortDescription: product.shortDescription || '', description: product.description || '',
      basePrice: product.basePrice ?? '', material: product.material || '',
      dimensions: product.dimensions || '', weight: product.weight || '',
      minOrderQuantity: product.minOrderQuantity ?? 1, maxOrderQuantity: product.maxOrderQuantity ?? 10000,
      leadTimeDays: product.leadTimeDays ?? 7,
      includesQrCode: product.includesQrCode ?? true, nfcEnabled: product.nfcEnabled ?? false,
      inStock: product.inStock ?? true,
      features: (product.features || []).join('\n'),
      colors: (product.colors || []).join(', '),
      tags: (product.tags || []).join(', '),
      tiers: (product.priceTiers || []).map(t => `${t.minQuantity}-${t.maxQuantity || ''}:${t.unitPrice}`).join('\n')
    });
  }, [product]);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        name: form.name, itemType: form.itemType,
        shortDescription: form.shortDescription, description: form.description,
        basePrice: Number(form.basePrice), material: form.material,
        dimensions: form.dimensions, weight: form.weight,
        minOrderQuantity: Number(form.minOrderQuantity), maxOrderQuantity: Number(form.maxOrderQuantity),
        leadTimeDays: Number(form.leadTimeDays),
        includesQrCode: form.includesQrCode, nfcEnabled: form.nfcEnabled, inStock: form.inStock,
        features: form.features.split('\n').map(s => s.trim()).filter(Boolean),
        colors: form.colors.split(',').map(s => s.trim()).filter(Boolean),
        tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
        priceTiers: form.tiers.split('\n').map(l => {
          const [range, price] = l.split(':');
          if (!range || !price) return null;
          const [min, max] = range.split('-');
          return { minQuantity: Number(min), maxQuantity: max ? Number(max) : undefined, unitPrice: Number(price) };
        }).filter(Boolean)
      };

      if (isEdit) {
        await updateProduct(product._id, data);
        toast.success('Product updated');
      } else {
        await createProduct(data);
        toast.success('Product created');
      }
      onSaved();
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const field = (label, key, type = 'text', extra = {}) => (
    <div>
      <label className="form-label">{label}</label>
      <input className="admin-input" type={type} value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} {...extra} />
    </div>
  );

  const toggle = (label, key) => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'6px 0' }}>
      <span style={{ fontSize:13 }}>{label}</span>
      <button type="button" onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
        style={{ width:38,height:20,background:form[key]?'var(--accent)':'var(--surface3)',border:'none',borderRadius:10,cursor:'pointer',position:'relative',padding:0 }}>
        <span style={{ position:'absolute',top:2,left:form[key]?20:2,width:16,height:16,background:'#fff',borderRadius:'50%',transition:'left .2s',boxShadow:'0 1px 3px rgba(0,0,0,.3)' }}/>
      </button>
    </div>
  );

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:580 }}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Product' : 'Add Product'}</h3>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:10 }}>
              {field('Product Name *', 'name', 'text', { required:true })}
              <div>
                <label className="form-label">Type *</label>
                <select className="admin-select" style={{ width:'100%' }} value={form.itemType}
                  onChange={e => setForm(f => ({ ...f, itemType: e.target.value }))}>
                  {ITEM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                </select>
              </div>
            </div>
            {field('Short Description', 'shortDescription')}
            <div>
              <label className="form-label">Full Description</label>
              <textarea className="admin-input" rows={3} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {field('Base Price ($) *', 'basePrice', 'number', { step:'0.01', min:0, required:true })}
              {field('Min Order Qty', 'minOrderQuantity', 'number', { min:1 })}
              {field('Lead Time (days)', 'leadTimeDays', 'number', { min:1 })}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {field('Material', 'material')}
              {field('Dimensions', 'dimensions')}
              {field('Weight', 'weight')}
            </div>
            {toggle('Includes QR Code', 'includesQrCode')}
            {toggle('NFC Enabled', 'nfcEnabled')}
            {toggle('In Stock', 'inStock')}
            <div>
              <label className="form-label">Features (one per line)</label>
              <textarea className="admin-input" rows={3} value={form.features}
                onChange={e => setForm(f => ({ ...f, features: e.target.value }))}
                placeholder="Waterproof silicone&#10;Laser-engraved QR&#10;Fits all collar sizes" />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {field('Colors (comma-separated)', 'colors')}
              {field('Tags (comma-separated)', 'tags')}
            </div>
            <div>
              <label className="form-label">Price Tiers (one per line: min-max:price)</label>
              <textarea className="admin-input" rows={3} value={form.tiers}
                onChange={e => setForm(f => ({ ...f, tiers: e.target.value }))}
                placeholder="1-49:2.99&#10;50-199:2.49&#10;200-:1.99" />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <span className="spinner" /> : isEdit ? 'Save' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Order Detail Modal ────────────────────────────────────────
function OrderModal({ order, onClose, onUpdated }) {
  const [form, setForm] = useState({
    status: order.status, trackingNumber: order.trackingNumber || '',
    carrier: order.carrier || '', shippingCost: order.shippingCost || 0,
    adminNotes: order.adminNotes || '', paymentStatus: order.paymentStatus || 'unpaid',
    statusNote: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateOrderStatus(order.orderNumber, form);
      toast.success('Order updated');
      onUpdated();
      onClose();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:580 }}>
        <div className="modal-header">
          <h3>Order {order.orderNumber}</h3>
          <span className={`badge ${STATUS_COLORS[order.status]}`}>{order.status}</span>
          <button className="btn-icon" style={{ marginLeft:'auto' }} onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div className="detail-grid">
            <div className="detail-field"><label>Organisation</label><span>{order.organizationName}</span></div>
            <div className="detail-field"><label>Contact</label><span>{order.contactName}</span></div>
            <div className="detail-field"><label>Email</label><span>{order.contactEmail}</span></div>
            <div className="detail-field"><label>Placed</label><span>{new Date(order.createdAt).toLocaleDateString()}</span></div>
          </div>

          <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'.6px', color:'var(--text3)' }}>Items</div>
          <table className="admin-table" style={{ fontSize:12 }}>
            <thead><tr><th>Product</th><th>Qty</th><th>Unit</th><th>Total</th></tr></thead>
            <tbody>
              {order.items.map((item, i) => (
                <tr key={i}>
                  <td>{item.productName}<br/><span style={{ fontSize:10, color:'var(--text3)' }}>{item.itemType}{item.nfcEnabled?' · NFC':''}</span></td>
                  <td>{item.quantity}</td>
                  <td>${item.unitPrice.toFixed(2)}</td>
                  <td>${item.lineTotal.toFixed(2)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight:700 }}>
                <td colSpan={3} style={{ textAlign:'right' }}>Subtotal</td>
                <td>${order.subtotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          {order.providerNotes && (
            <div style={{ background:'var(--surface2)', borderRadius:'var(--radius)', padding:10, fontSize:12 }}>
              <strong>Provider Notes:</strong> {order.providerNotes}
            </div>
          )}

          <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'.6px', color:'var(--text3)', marginTop:8 }}>Update Order</div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div>
              <label className="form-label">Status</label>
              <select className="admin-select" style={{ width:'100%' }} value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                {ORDER_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Payment</label>
              <select className="admin-select" style={{ width:'100%' }} value={form.paymentStatus}
                onChange={e => setForm(f => ({ ...f, paymentStatus: e.target.value }))}>
                {['unpaid','invoiced','paid','refunded'].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
            <div><label className="form-label">Tracking #</label><input className="admin-input" value={form.trackingNumber} onChange={e => setForm(f => ({ ...f, trackingNumber: e.target.value }))} /></div>
            <div><label className="form-label">Carrier</label><input className="admin-input" value={form.carrier} onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))} /></div>
            <div><label className="form-label">Shipping $</label><input className="admin-input" type="number" step="0.01" value={form.shippingCost} onChange={e => setForm(f => ({ ...f, shippingCost: e.target.value }))} /></div>
          </div>
          <div><label className="form-label">Status Note</label><input className="admin-input" value={form.statusNote} onChange={e => setForm(f => ({ ...f, statusNote: e.target.value }))} placeholder="e.g. Shipped via FedEx" /></div>
          <div><label className="form-label">Admin Notes</label><textarea className="admin-input" rows={2} value={form.adminNotes} onChange={e => setForm(f => ({ ...f, adminNotes: e.target.value }))} /></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            {loading ? <span className="spinner" /> : 'Update Order'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function AdminWholesale() {
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editProduct, setEditProduct] = useState(null);
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [viewOrder, setViewOrder] = useState(null);
  const [orderFilter, setOrderFilter] = useState('');

  const loadProducts = useCallback(() => {
    getAdminProducts().then(r => setProducts(r.data.products)).catch(() => toast.error('Failed to load products'));
  }, []);

  const loadOrders = useCallback(() => {
    Promise.all([
      getAdminOrders({ status: orderFilter || undefined }),
      getOrderStats()
    ]).then(([o, s]) => {
      setOrders(o.data.orders);
      setStats(s.data.stats);
    }).catch(() => toast.error('Failed to load orders'));
  }, [orderFilter]);

  useEffect(() => {
    Promise.all([
      getAdminProducts().then(r => setProducts(r.data.products)),
      getAdminOrders().then(r => setOrders(r.data.orders)),
      getOrderStats().then(r => setStats(r.data.stats))
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleDeleteProduct = async id => {
    if (!window.confirm('Remove this product from the catalog?')) return;
    try { await deleteProduct(id); toast.success('Product removed'); loadProducts(); }
    catch { toast.error('Failed'); }
  };

  const byStatus = key => stats?.byStatus?.find(s => s._id === key);

  return (
    <AdminLayout title="Wholesale">
      <div style={{ display:'flex', gap:0, borderBottom:'1px solid var(--border)', marginBottom:20 }}>
        {['products','orders'].map(t => (
          <button key={t} onClick={() => { setTab(t); if(t==='orders') loadOrders(); }}
            style={{ background:'none',border:'none',cursor:'pointer',padding:'10px 20px',fontSize:14,fontWeight:600,textTransform:'capitalize',fontFamily:'inherit',
            color:tab===t?'var(--accent)':'var(--text3)',borderBottom:tab===t?'2px solid var(--accent)':'2px solid transparent',marginBottom:-1 }}>
            {t}
          </button>
        ))}
      </div>

      {/* Products Tab */}
      {tab === 'products' && (
        <>
          <div className="toolbar" style={{ marginBottom:16 }}>
            <span style={{ fontSize:12, color:'var(--text3)', flex:1 }}>{products.length} products</span>
            <button className="btn btn-primary" onClick={() => setShowNewProduct(true)}>+ Add Product</button>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Type</th><th>Base Price</th><th>Min Qty</th><th>QR</th><th>NFC</th><th>Stock</th><th></th></tr></thead>
              <tbody>
                {loading ? Array.from({length:4}).map((_,i) => <tr key={i}>{Array.from({length:8}).map((_,j) => <td key={j}><div className="skeleton" style={{height:14,width:'80%'}}/></td>)}</tr>) :
                products.length === 0 ? <tr><td colSpan={8}><div className="empty-state"><div className="empty-icon">📦</div><p>No products yet. Add your first product.</p></div></td></tr> :
                products.map(p => (
                  <tr key={p._id} style={{ opacity: p.isActive ? 1 : .5 }}>
                    <td style={{ fontWeight:600 }}>{p.name}</td>
                    <td style={{ textTransform:'capitalize', color:'var(--text2)' }}>{p.itemType}</td>
                    <td>${p.basePrice?.toFixed(2)}</td>
                    <td>{p.minOrderQuantity}</td>
                    <td><span className={`badge ${p.includesQrCode?'badge-green':'badge-grey'}`}>{p.includesQrCode?'Yes':'No'}</span></td>
                    <td><span className={`badge ${p.nfcEnabled?'badge-green':'badge-grey'}`}>{p.nfcEnabled?'Yes':'No'}</span></td>
                    <td><span className={`badge ${p.inStock?'badge-green':'badge-red'}`}>{p.inStock?'In Stock':'OOS'}</span></td>
                    <td>
                      <div style={{ display:'flex', gap:4 }}>
                        <button className="btn btn-sm btn-secondary" onClick={() => setEditProduct(p)}>Edit</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteProduct(p._id)}>✕</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Orders Tab */}
      {tab === 'orders' && (
        <>
          {stats && (
            <div className="stat-grid" style={{ marginBottom:16 }}>
              <div className="stat-card"><div className="stat-label">Total Revenue</div><div className="stat-value" style={{ fontSize:24 }}>${stats.totalRevenue?.toFixed(2) || '0.00'}</div></div>
              <div className="stat-card green"><div className="stat-label">Total Orders</div><div className="stat-value" style={{ fontSize:24 }}>{stats.totalOrders || 0}</div></div>
              <div className="stat-card yellow"><div className="stat-label">Pending</div><div className="stat-value" style={{ fontSize:24 }}>{byStatus('pending')?.count || 0}</div></div>
              <div className="stat-card purple"><div className="stat-label">Last 30 Days</div><div className="stat-value" style={{ fontSize:24 }}>{stats.recentOrders || 0}</div></div>
            </div>
          )}

          <div className="toolbar" style={{ marginBottom:16 }}>
            <select className="admin-select" value={orderFilter} onChange={e => { setOrderFilter(e.target.value); }}>
              <option value="">All Statuses</option>
              {ORDER_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
            <button className="btn btn-sm btn-secondary" onClick={loadOrders}>↻ Refresh</button>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Order #</th><th>Organisation</th><th>Items</th><th>Total</th><th>Status</th><th>Payment</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {orders.length === 0 ? <tr><td colSpan={8}><div className="empty-state"><div className="empty-icon">📋</div><p>No orders yet.</p></div></td></tr> :
                orders.map(o => (
                  <tr key={o._id}>
                    <td className="mono" style={{ fontSize:12 }}>{o.orderNumber}</td>
                    <td style={{ fontWeight:600 }}>{o.organizationName}</td>
                    <td>{o.items.reduce((s,i) => s+i.quantity, 0)} items</td>
                    <td style={{ fontWeight:600 }}>${o.totalAmount.toFixed(2)}</td>
                    <td><span className={`badge ${STATUS_COLORS[o.status]}`}>{o.status}</span></td>
                    <td><span className={`badge ${o.paymentStatus==='paid'?'badge-green':o.paymentStatus==='invoiced'?'badge-yellow':'badge-grey'}`}>{o.paymentStatus}</span></td>
                    <td style={{ fontSize:12, color:'var(--text3)' }}>{new Date(o.createdAt).toLocaleDateString()}</td>
                    <td><button className="btn btn-sm btn-secondary" onClick={() => setViewOrder(o)}>Manage</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {(showNewProduct || editProduct) && (
        <ProductModal product={editProduct || {}} onClose={() => { setShowNewProduct(false); setEditProduct(null); }} onSaved={loadProducts} />
      )}
      {viewOrder && <OrderModal order={viewOrder} onClose={() => setViewOrder(null)} onUpdated={loadOrders} />}
    </AdminLayout>
  );
}
